import React, { useState, useEffect, useRef } from "react";
import { Card, Checkbox, Skeleton, Button, Modal, Row, Col } from "antd";
import { MsgBox } from "../../../../shared/custom-ant-d";
import W4FormStep1 from "./_W4FormStep1";
import W4FormStep2 from "./_W4FormStep2";
import W4FormStep3 from "./_W4FormStep3";
import W4FormStep4 from "./_W4FormStep4";
import SignPad from "./_SignPad";
import { reqGetW4Form, reqPostSaveW4Form } from "../../../../axios/api";

const UseFocus = () => {
  const htmlElRef = useRef(null);
  const setFocus = () => {
    htmlElRef.current && htmlElRef.current.focus();
  };
  return [htmlElRef, setFocus];
};
export const W4Form = (props) => {
  const { formData, setShowW4Form, fetchListOnboarding } = props;

  const [w4Form, setW4Form] = useState(null);
  const [showSignPad, setShowSignPad] = useState(false);
  const [showInstruction, setShowInstruction] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [hasErrors, setHasErrors] = useState({
    confirmExemptionConditionsMetInd: false,
    isAgreed: false,
    employeeSignature: false,
  });
  const [disableAll, setDisableAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [
    focusConfirmExemptionConditionsMetInd,
    setFocusConfirmExemptionConditionsMetInd,
  ] = UseFocus();
  const [focusIsAgreed, setFocusIsAgreed] = UseFocus();
  const [focusEmployeeSignature, setFocusEmployeeSignature] = UseFocus();

  const resetW4 = (isChecked) => {
    const payload = {
      isSingle: false,
      isMarried: false,
      isHeadOfHouseHold: false,
      claimExemptionFromWithholdingInd: false,
      confirmExemptionConditionsMetInd: false,
      multipleJobsOnlyInd: false,
      step2WithholdAmount: null,
      confirmExemptionConditionsMetInd: false,
      claimExemptionFromWithholdingInd: isChecked,
      dependentsChildrenCount: 0,
      dependentsChildrenAmount: null,
      dependentsOtherCount: 0,
      dependentsOtherAmount: null,
      taxCreditsAmount: null,
      dependentsTotalAmount: null,
      otherIncome: null,
      deductions: null,
      employeeSignature: null,
      signatureDt: null,
      extraWithholding: null,
    };
    setW4Form({ ...w4Form, ...payload });
    setIsAgreed(false);
    setShowSignPad(false);
  };

  const StepProps = {
    w4Form,
    focusConfirmExemptionConditionsMetInd,
    hasErrors,
    disableAll,
    setW4Form,
    resetW4,
    setHasErrors,
  };

  useEffect(() => {
    if (formData != null) {
      reqGetW4Form(formData.OnboardingID)
        .then((res) => {
          if (res.data.isSuccess) {
            setW4Form(res.data.w4Form);
            if (res.data.w4Form.employeeSignature != null) {
              setIsAgreed(true);
              setShowSignPad(true);
            } else {
              setIsAgreed(false);
              setShowSignPad(false);
            }
            if (res.data.w4Form.signatureDt != null) {
              setDisableAll(true);
            }
          } else {
            MsgBox({
              type: "error",
              message: "",
              description: res.data.errorMessage,
            });
          }
          //setDisableAll(false); //remove comment to test lock form
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [formData]);

  useEffect(() => {
    setShowSignPad(isAgreed);
    if (isAgreed) {
      setHasErrors({ ...hasErrors, ...{ isAgreed: false } });
    }
  }, [isAgreed]);
  const handleSubmit = () => {
    if (
      w4Form.claimExemptionFromWithholdingInd &&
      !w4Form.confirmExemptionConditionsMetInd
    ) {
      setHasErrors({
        ...hasErrors,
        ...{ confirmExemptionConditionsMetInd: true },
      });
      setFocusConfirmExemptionConditionsMetInd();
      return false;
    }

    if (!isAgreed) {
      setHasErrors({ ...hasErrors, ...{ isAgreed: true } });
      setFocusIsAgreed();
      return false;
    }

    if (w4Form.employeeSignature == null) {
      setHasErrors({ ...hasErrors, ...{ employeeSignature: true } });
      setFocusEmployeeSignature();
      return false;
    }

    setIsLoading(true);
    const payloadStr = JSON.stringify({
      ...w4Form,
      ...{
        step2WithholdAmount:
          w4Form.step2WithholdAmount !== null
            ? `${w4Form.step2WithholdAmount}`
            : null,
        taxCreditsAmount:
          w4Form.taxCreditsAmount !== null
            ? `${w4Form.taxCreditsAmount}`
            : null,
        otherIncome:
          w4Form.otherIncome !== null ? `${w4Form.otherIncome}` : null,
        deductions: w4Form.deductions !== null ? `${w4Form.deductions}` : null,
        extraWithholding:
          w4Form.extraWithholding !== null
            ? `${w4Form.extraWithholding}`
            : null,
      },
    });

    reqPostSaveW4Form(payloadStr)
      .then((res) => {
        if (res.data.isSuccess) {
          MsgBox({
            type: "success",
            message: "",
            description: "Your form has been submitted.",
          });
          fetchListOnboarding();
          setShowW4Form(false);
        } else {
          MsgBox({
            type: "error",
            message: "",
            description: res.data.errorMessage,
          });
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <Card type="inner">
        <h6>W4 Instructions</h6>
        <b>U.S. Citizens and Permanent Residents</b>
        <p>
          All employees must complete IRS Form W-4, Employee’s Withholding
          Certificate, so that an employer may know how much income tax to
          withhold from employees’ wages. The amount of income tax withholding
          must be based on marital status and withholding allowances.
        </p>
        <p>
          Employees may not base their withholding amounts on a fixed dollar
          amount or percentage. However, the employees may specify a dollar
          amount to be withheld in addition to the amount of withholding based
          on marital status and other claims.
        </p>
        <b>Exemption From Income Tax Withholding</b>
        <p>
          Generally, an employee may claim exemption from income tax withholding
          because he or she had no income tax liability last year and expects
          none this year.
          <Button
            type="link"
            onClick={() => {
              setShowInstruction(true);
            }}
          >
            See the Form W-4 instructions for more information.
          </Button>
        </p>
        <b>Assistance With Tax Withholding Calculation</b>
        <p>
          The IRS has developed an application that assists employees when they
          complete IRS Form W-4. Using the on-line calculator ensures that
          employees do not have too much or too little income tax withheld from
          their paychecks.
          {"  "}
          <a
            href="https://www.irs.gov/individuals/tax-withholding-estimator"
            target="_blank"
          >
            See IRS Withholding Calculator.
          </a>{" "}
          In cases of complicated tax situations, the IRS does not recommend the
          use of the IRS Calculator. Instead employees should read
          {"  "}
          <a href="https://www.irs.gov/pub/irs-pdf/p505.pdf" target="_blank">
            IRS Publication 505, Tax Withholding and Estimated Tax
          </a>
          If you would like a copy of the W-4 click
          {"  "}
          <a href="https://www.irs.gov/pub/irs-pdf/fw4.pdf" target="_blank">
            Print W4.
          </a>
        </p>
        <Modal
          title="Form W-4 instructions"
          visible={showInstruction}
          width={1000}
          bodyStyle={{ height: 300, overflow: "scroll" }}
          closable={false}
          footer={[
            <Button
              key="back"
              onClick={() => {
                setShowInstruction(false);
              }}
              type="danger"
            >
              Close
            </Button>,
          ]}
        >
          <b>General Instructions</b>
          <br />
          <b>Future Developments</b>
          <br />
          <br />
          <p>
            For the latest information about developments related to Form W-4,
            such as legislation enacted after it was published, go to{" "}
            <a href="http://www.irs.gov/FormW4" target="_blank">
              www.irs.gov/FormW4
            </a>
            .
          </p>
          <p>Purpose of Form</p>
          <p>
            Complete Form W-4 so that your employer can withhold the correct
            federal income tax from your pay. If too little is withheld, you
            will generally owe tax when you file your tax return and may owe a
            penalty. If too much is withheld, you will generally be due a
            refund. Complete a new Form W-4 when changes to your personal or
            financial situation would change the entries on the form. For more
            information on withholding and when you must furnish a new Form W-4,
            see Pub. 505, Tax Withholding and Estimated Tax.
          </p>
          <p>
            Exemption from withholding. You may claim exemption from withholding
            for 2021 if you meet both of the following conditions: you had no
            federal income tax liability in 2020 and you expect to have no
            federal income tax liability in 2021. You had no federal income tax
            liability in 2020 if (1) your total tax on line 24 on your 2020 Form
            1040 or 1040-SR is zero (or less than the sum of lines 27, 28, 29,
            and 30), or (2) you were not required to file a return because your
            income was below the filing threshold for your correct filing
            status. If you claim exemption, you will have no income tax withheld
            from your paycheck and may owe taxes and penalties when you file
            your 2021 tax return. To claim exemption from withholding, certify
            that you meet both of the conditions above by writing "Exempt" on
            Form W-4 in the space below Step 4(c). Then, complete Steps 1(a),
            1(b), and 5. Do not complete any other steps. You will need to
            submit a new Form W-4 by February 15, 2022.
          </p>
          <p>
            Your privacy. If you prefer to limit information provided in Steps 2
            through 4, use the online estimator, which will also increase
            accuracy.
          </p>
          <p>
            As an alternative to the estimator: if you have concerns with Step
            2(c), you may choose Step 2(b); if you have concerns with Step 4(a),
            you may enter an additional amount you want withheld per pay period
            in Step 4(c). If this is the only job in your household, you may
            instead check the box in Step 2(c), which will increase your
            withholding and significantly reduce your paycheck (often by
            thousands of dollars over the year).
          </p>
          <p>
            When to use the estimator. Consider using the estimator at{" "}
            <a href="http://www.irs.gov/W4App" target="_blank">
              www.irs.gov/W4App
            </a>{" "}
            if you:
          </p>

          <p>
            Expect to work only part of the year;Have dividend or capital gain
            income, or are subject to additional taxes, such as Additional
            Medicare tax; Have self-employment income (see below); or Prefer the
            most accurate withholding for multiple job situations.
            Self-employment. Generally, you will owe both income and
            self-employment taxes on any self-employment income you receive
            separate from the wages you receive as an employee. If you want to
            pay these taxes through withholding from your wages, use the
            estimator at{" "}
            <a href="http://www.irs.gov/W4App" target="_blank">
              www.irs.gov/W4App
            </a>{" "}
            to figure the amount to have withheld.
          </p>

          <p>
            Nonresident alien. If you're a nonresident alien, see Notice 1392,
            Supplemental Form W-4 Instructions for Nonresident Aliens, before
            completing this form.
          </p>

          <b>Specific Instructions</b>
          <ul>
            <li>
              Step 1(c). Check your anticipated filing status. This will
              determine the standard deduction and tax rates used to compute
              your withholding.
            </li>
            <li>
              <p>
                Step 2. Use this step if you:
                <br />
                (1) have more than one job at the same time, or <br />
                (2) are married filing jointly and you and your spouse both
                work. <br />
                Option (a) most accurately calculates the additional tax you
                need to have withheld, while option (b) does so with a little
                less accuracy. If you (and your spouse) have a total of only two
                jobs, you may instead check the box in option (c). The box must
                also be checked on the Form W-4 for the other job. If the box is
                checked, the standard deduction and tax brackets will be cut in
                half for each job to calculate withholding. This option is
                roughly accurate for jobs with similar pay; otherwise, more tax
                than necessary may be withheld, and this extra amount will be
                larger the greater the difference in pay is between the two
                jobs. Multiple jobs. Complete Steps 3 through 4(b) on only one
                Form W-4. Withholding will be most accurate if you do this on
                the Form W-4 for the highest paying job.
              </p>
            </li>
            <li>
              <p>
                Step 3. This step provides instructions for determining the
                amount of the child tax credit and the credit for other
                dependents that you may be able to claim when you file your tax
                return. To qualify for the child tax credit, the child must be
                under age 17 as of December 31, must be your dependent who
                generally lives with you for more than half the year, and must
                have the required social security number. You may be able to
                claim a credit for other dependents for whom a child tax credit
                can't be claimed, such as an older child or a qualifying
                relative. For additional eligibility requirements for these
                credits, see Pub. 972, Child Tax Credit and Credit for Other
                Dependents. You can also include other tax credits in this step,
                such as education tax credits and the foreign tax credit. To do
                so, add an estimate of the amount for the year to your credits
                for dependents and enter the total amount in Step 3. Including
                these credits will increase your paycheck and reduce the amount
                of any refund you may receive when you file your tax return.
              </p>
            </li>
            <li>
              <p>Step 4 (optional).</p>
              <ul>
                <li>
                  Step 4(a). Enter in this step the total of your other
                  estimated income for the year, if any. You shouldn't include
                  income from any jobs or self-employment. If you complete Step
                  4(a), you likely won't have to make estimated tax payments for
                  that income. If you prefer to pay estimated tax rather than
                  having tax on other income withheld from your paycheck, see
                  Form 1040-ES, Estimated Tax for Individuals.
                </li>
                <li>
                  {" "}
                  Step 4(b). Enter in this step the amount from the Deductions
                  Worksheet, line 5, if you expect to claim deductions other
                  than the basic standard deduction on your 2021 tax return and
                  want to reduce your withholding to account for these
                  deductions. This includes both itemized deductions and other
                  deductions such as for student loan interest and IRAs.
                </li>
                <li>
                  {" "}
                  Step 4(c). Enter in this step any additional tax you want
                  withheld from your pay each pay period, including any amounts
                  from the Multiple Jobs Worksheet, line 4. Entering an amount
                  here will reduce your paycheck and will either increase your
                  refund or reduce any amount of tax that you owe.
                </li>
              </ul>
            </li>
          </ul>
        </Modal>
      </Card>
      <Card type="inner">
        <h6>Fill in your W-4 details here:</h6>
      </Card>
      {w4Form != null ? <W4FormStep1 {...StepProps} /> : <Skeleton active />}
      {w4Form != null ? (
        <W4FormStep2
          {...{ ...StepProps }}
          disableAll={
            disableAll ||
            w4Form.claimExemptionFromWithholdingInd === true ||
            (w4Form.isSingle === false &&
              w4Form.isMarried === false &&
              w4Form.isHeadOfHouseHold === false)
          }
        />
      ) : (
        <Skeleton active />
      )}
      {w4Form != null ? (
        <W4FormStep3
          {...{ ...StepProps }}
          disableAll={
            disableAll ||
            w4Form.claimExemptionFromWithholdingInd === true ||
            (w4Form.isSingle === false &&
              w4Form.isMarried === false &&
              w4Form.isHeadOfHouseHold === false)
          }
        />
      ) : (
        <Skeleton active />
      )}
      {w4Form != null ? (
        <W4FormStep4
          {...{ ...StepProps }}
          disableAll={
            disableAll ||
            w4Form.claimExemptionFromWithholdingInd === true ||
            (w4Form.isSingle === false &&
              w4Form.isMarried === false &&
              w4Form.isHeadOfHouseHold === false)
          }
        />
      ) : (
        <Skeleton active />
      )}
      {w4Form != null ? (
        <Card type="inner">
          <div style={{ textAlign: "center" }}>
            <Checkbox
              ref={focusIsAgreed}
              checked={isAgreed}
              onChange={(e) => {
                setIsAgreed(e.target.checked);
              }}
              disabled={disableAll}
            >
              I agree that the declarations listed above are accurate.
            </Checkbox>
            {hasErrors.isAgreed ? (
              <p style={{ color: "red" }}>This confirmation is required.</p>
            ) : (
              <></>
            )}
            <div ref={focusEmployeeSignature}>
              <SignPad
                {...{ showSignPad, setShowSignPad }}
                isModal={false}
                disabled={disableAll}
                signature={
                  w4Form.employeeSignature == null
                    ? null
                    : "data:image/png;base64," + w4Form.employeeSignature
                }
                onSignPadOk={(e) => {
                  setW4Form({
                    ...w4Form,
                    ...{
                      employeeSignature:
                        e != null
                          ? e.replace("data:image/png;base64,", "")
                          : null,
                    },
                  });
                  setHasErrors({
                    ...hasErrors,
                    ...{ employeeSignature: false },
                  });
                }}
              />
              {hasErrors.employeeSignature ? (
                <p style={{ color: "red", paddingRight: "50px" }}>
                  Signature is required.
                </p>
              ) : (
                <></>
              )}
            </div>
          </div>
        </Card>
      ) : (
        <Skeleton active />
      )}
      <Card type="inner">
        <p>
          Need help figuring out your allowances and deductions? You can
          download W-4 form using the following link -{" "}
          <a href="https://www.irs.gov/pub/irs-pdf/fw4.pdf" target="_blank">
            https://www.irs.gov/pub/irs-pdf/fw4.pdf
          </a>
          ){" "}
        </p>
        <p>
          Go to page# 3 of W-4 form and use Step 2(b) worksheet to determine
          accurate withholding for multiple jobs or if spouse works
        </p>
        <p>
          Go to page# 3 of W-4 form and use Step 4(b) worksheet to claim
          deductions orther than the standard deduction and want to reduce your
          withholding
        </p>
      </Card>
      <Card type="inner">
        <Row justify="center">
          <Col md={{ span: 4 }}>
            <Button
              loading={isLoading}
              type="danger"
              htmlType="button"
              onClick={() => {
                setShowW4Form(false);
              }}
            >
              Back
            </Button>
            {"  "}
            <Button
              loading={isLoading}
              type="danger"
              htmlType="button"
              disabled={disableAll}
              onClick={() => {
                handleSubmit();
              }}
            >
              Submit
            </Button>
          </Col>
        </Row>
      </Card>
    </>
  );
};
